import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextInput from "../../../components/inputs/TextInput";
import useAuthStore from "../../../store/Auth";
import { BuildingsConfigurationSchema } from "../../../schemas/buildingsConfigurationSchema";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { mapToOptions } from "../../../lib/functions";
import {
  useGetAllBuildings,
  useGetAllLocationsDropdown,
  useGetBuildingById,
} from "../../../hooks/common/query";
import BuildingsConfigurationTable from "../../../components/table/BuildingsConfigurationTable";
import {
  useAddBuildingAttachment,
  useAddUpdateBuilding,
  useGetBuildingFile,
  useIsBuildingExists,
} from "../../../hooks/common/mutation";
import {
  allowedImagesTypes,
  ATTACHMENTS_TYPE,
  ContentType,
  PAGE_SIZE,
} from "../../../constants/common";
import { Building } from "../../../hooks/common/interface";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";

interface BuildingsConfigurationData {
  buildingNameEn: string;
  buildingNameAr: string;
  location: string;
  description?: string | null;
  link: string;
  locationPhoto?: any;
}
const BuildingsConfiguration = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [allBuildings, setAllBuildings] = useState<Building[]>([]);

  const [havePhoto, setHavePhoto] = useState(0);

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
  } = useFormWithSchema(BuildingsConfigurationSchema, {
    context: { havePhoto: !!havePhoto },
  });

  const [selectedBuildingId, setSelectedBuildingId] = useState(0);

  const uploadPhotoRef = useRef<HTMLInputElement | null>(null);

  const [photoFileName, setPhotoFileName] = useState("");
  const [photoFileType, setPhotoFileType] = useState("");
  const [selectedType, setSelectedType] = useState(0);
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);

  const { mutateAsync: IsBuildingExists } = useIsBuildingExists();
  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: buildings } = useGetAllBuildings({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (buildings) {
      setAllBuildings(buildings.data || []);
      setPages(buildings.pagination?.pages || 1);
      setCount(buildings.pagination?.count || 0);
    }
  }, [setPages, buildings]);

  const {
    mutateAsync: AddUpdateBuilding,
    isPending: isAddUpdateBuildingLoading,
  } = useAddUpdateBuilding();

  const { mutateAsync: GetBuildingFile, isPending: isGetBuildingFileLoading } =
    useGetBuildingFile();

  const { mutateAsync: AddBuildingAttachment } = useAddBuildingAttachment();

  const user = useAuthStore((state) => state.user);

  const { data: selectedBuilding } = useGetBuildingById({
    encryptedId: selectedBuildingId ? selectedBuildingId.toString() : "",
  });

  useEffect(() => {
    if (selectedBuilding) {
      setSelectedBuildingId(selectedBuilding.n_BuildingID);
      setValue("buildingNameEn", selectedBuilding.s_BuildingName_En);
      setValue("buildingNameAr", selectedBuilding.s_BuildingName_Ar);
      setValue("location", selectedBuilding.n_LocationID);
      setValue("description", selectedBuilding.s_Description);
      setValue("link", selectedBuilding.s_MapLocationLink);
      setHavePhoto(selectedBuilding.havePhoto);
    }
  }, [selectedBuilding, setValue]);

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        if (selectedBuildingId) {
          setSelectedType(type);
          const body = {
            id: selectedBuildingId,
            type: "Photo",
          };
          const res = await GetBuildingFile(body);
          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        setSelectedType(0);
      }
    },
    [GetBuildingFile, selectedBuildingId, t]
  );

  const onClear = useCallback(() => {
    setSelectedBuildingId(0);

    resetField("buildingNameAr");
    resetField("buildingNameEn");
    resetField("description");
    resetField("location");
    resetField("link");
    resetField("locationPhoto");

    setPhotoFileName("");
    setPhotoFileType("");

    setHavePhoto(0);
    if (uploadPhotoRef.current) {
      uploadPhotoRef.current.value = "";
    }
  }, [resetField]);

  const onSubmit = useCallback(
    async (data: BuildingsConfigurationData) => {
      try {
        const isExistBody = {
          n_LocationID: Number(data.location),
          n_BuildingID: selectedBuildingId,
          s_BuildingName_Ar: data.buildingNameAr,
          s_BuildingName_En: data.buildingNameEn,
        };
        const { isExists } = await IsBuildingExists(isExistBody);

        if (isExists) {
          toast.error(t("buildingExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        return;
      }

      let res;

      try {
        if (user?.user_id) {
          const body = {
            n_BuildingID: 0,
            s_BuildingName_Ar: data.buildingNameAr,
            s_BuildingName_En: data.buildingNameEn,
            s_Description: data.description,
            n_LocationID: Number(data.location),
            s_MapLocationLink: data.link,
            n_UserID: user?.user_id,
          };
          if (selectedBuildingId === 0) {
            res = await AddUpdateBuilding(body);
          } else {
            const updateBody = {
              ...body,
              n_BuildingID: selectedBuildingId,
            };
            res = await AddUpdateBuilding(updateBody);
          }
        }
        onClear();
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }

      try {
        if (res && data.locationPhoto) {
          const buildingId = res.id;

          const formData = new FormData();
          formData.append("Id", buildingId.toString());
          formData.append("File", data.locationPhoto);
          formData.append("S_FileType", photoFileType);
          formData.append("S_FileFieldName", "S_MapLocationFile");
          formData.append("S_FiledFileType", "S_MapLocationContentType");

          await AddBuildingAttachment(formData);
        }

        if (selectedBuildingId === 0) {
          toast.success(t("buildingAdded"));
        } else {
          toast.success(t("buildingUpdated"));
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        return;
      }
    },
    [
      selectedBuildingId,
      IsBuildingExists,
      t,
      user?.user_id,
      onClear,
      AddUpdateBuilding,
      photoFileType,
      AddBuildingAttachment,
    ]
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("buildings")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col gap-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 ">
              <Controller
                name="buildingNameEn"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("buildingNameEn")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                )}
              />

              <Controller
                name="buildingNameAr"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("buildingNameAr")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    required
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="link"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("locationLink")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <TextInput
                      label={t("description")}
                      onChange={onChange}
                      value={value || ""}
                      error={errors?.[name]?.message}
                    />
                  );
                }}
              />

              <FileUploadInput
                name="locationPhoto"
                label={t("locationPhoto")}
                accept=".png,.jpg,.jpeg"
                allowedTypes={allowedImagesTypes}
                isAttachments={havePhoto}
                control={control}
                uploadRef={uploadPhotoRef}
                fileName={photoFileName}
                setFileName={setPhotoFileName}
                setFileType={setPhotoFileType}
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={ATTACHMENTS_TYPE.PHOTO}
                isGetFileByTypeLoading={isGetBuildingFileLoading}
                error={errors.locationPhoto}
                S_FileFieldName="locationPhoto"
                s_FiledFileType="_"
                onRemoveAttachment={() => setHavePhoto(0)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button isLoading={isAddUpdateBuildingLoading}>
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>

          <BuildingsConfigurationTable
            buildings={allBuildings}
            setSelectedBuildingId={setSelectedBuildingId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default BuildingsConfiguration;
